import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { EChartsOption } from 'echarts';
import * as echarts from 'echarts';
import { VerticalGraph, VerticalGraphItem, VerticalGraphType } from 'src/app/model/vertical-views';


function truncateDecimal(value: number): string {
  return value.toFixed(2).replace(/\.?0+$/, '')
}

@Component({
  selector: 'app-vertical-bar-graph',
  templateUrl: './vertical-bar-graph.component.html',
  styleUrls: ['./vertical-bar-graph.component.scss']
})
export class VerticalBarGraphComponent implements OnInit {

  @Input() type: VerticalGraphType = VerticalGraphType.SIMPLE_BAR;
  @Input() isSecondary: boolean = false;
  @Input() userData: any;
  @Output() onBarSelect: EventEmitter<any> = new EventEmitter<any>();
  @Output() onUserBarSelect: EventEmitter<any> = new EventEmitter<any>();

  @Input() selectedColumnIndex?: number;

  private _graph: VerticalGraph = {} as VerticalGraph;

  @Input() set graph(value: VerticalGraph) {
    this._graph = value;
    this.initGraphOption();
    if (this.selectedColumnIndex !== undefined && this.selectedColumnIndex !== -1 && this.myChart) {
      this.selectBar(this.selectedColumnIndex);
    } else {
      this.selectDefaultBar();
    }
  }
  get graph() {
    return this._graph;
  }

  public myChart: any;
  public options: EChartsOption = {};
  public mergeOptions: EChartsOption = {};

  private baseOptions: any = {
    textStyle: {
      color: '#768B95',
      fontSize: 10,
      fontFamily: 'InterRegular',
    },
    grid: {
      left: '10.6%',
      right: 10,
      top: 45,
      bottom: 25,
      containLabel: true,
    },
    graphic: [
      {
        elements: [
          {
            type: 'line',
            bottom: 52,
            zlevel: 1,
            shape: {
              x1: -10,
              y1: 0,
              x2: 10000,
              y2: 0
            },
            style: {
              stroke: '#294E67',
            },
          },
        ],
      },
    ],
    dataset: {},
    xAxis: [
      {
        type: 'category',
        axisLine: {
          lineStyle: {
            color: '#294E67',
          }
        },
        axisTick: {
          interval: 0,
          height: 6
        },
        data: []
      },
      {
        id: 'preliminary',
        type: 'category',
        axisLine: {
          show: false,
          onZero: false,
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          margin: 0,
          color: '#768B95',
          fontSize: 10,
          fontFamily: 'InterMedium',
          borderColor: '#1A4F69',
          align: 'center',
          verticalAlign: 'center',
          borderWidth: 1,
          borderType: 'solid',
          borderRadius: 4,
          backgroundColor: '#002949',
          padding: [1, 4, 2, 4],
          formatter: function (params: any) {
            return params && params;
          }
        },
        position: 'bottom',
        offset: 30,
        data: []
      },
      {
        id: 'colGroupName',
        type: 'category',
        axisLine: {
          show: false,
          onZero: false,
        },
        axisTick: {
          show: false,
          length: 12,
          lineStyle: {
            color: '#1A4F69'
          }
        },
        axisLabel: {
          margin: 0
        },
        position: 'bottom',
        offset: 30,
      }
    ],
    yAxis: {
      type: 'value',
      position: 'right',
      splitNumber: 3,
      axisLabel: {
        margin: 25,
        color: '#4E738B',
        fontSize: '11px',
        fontFamily: 'InterRegular',
        formatter: (value: number, index: number) => this.formatYAxisLabel(value, index)
      },
      splitLine: {
        lineStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#002949' }, { offset: 0.5, color: '#004A6A' }, { offset: 1, color: '#002949 ' }]),
        },
      },
    },
  };

  private optionSimpleBar: any = {
    color: [
      new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#00567B' }, { offset: 1, color: '#004764' },]),
    ],
    series: [
      {
        id: 'current',
        name: 'current',
        type: 'bar',
        label: {
          position: 'top',
          fontSize: 16,
          fontFamily: 'DemoMedium',
          color: '#00BDDE',
          align: 'center',
          formatter: function (params: any): string {
            let label = truncateDecimal(params.value);
            label += params.data["percent"] === 0 || params.data["percent"] ? `\n{percent|${params.data["percent"]}%}` : '';
            return label;
          },
          rich: {
            percent: {
              color: '#00DDED',
              fontSize: 12,
              fontFamily: 'DemoLight',
            },
          }
        },
        itemStyle: {
          shadowColor: 'rgba(0,33,59,0.6)',
          shadowBlur: 8,
        },
        selectedMode: true,
        select: {
          itemStyle: {
            borderWidth: 0,
            color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#00DEEF' }, { offset: 1, color: '#1A4F69' }])
          },
          label: {
            show: true,
          }
        },
        data: []
      },
      {
        type: 'bar',
        xAxisIndex: 2,
        dimensions: ["colGroupName"]
      },
    ]
  };

  private optionCompareBar: any = {
    color: [
      new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#006F9F' }, { offset: 1, color: '#1A4F69' },]),
      new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#00567B' }, { offset: 1, color: '#1A4F69' },]),
      new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: 'rgba(209,220,221,0.18)' }, { offset: 1, color: '#799A9D' },]),
    ],
    series: [
      {
        id: 'compare',
        type: 'bar',
        barGap: 0,
        stack: 'x',
        label: {
          position: 'top',
          fontSize: 16,
          fontFamily: 'DemoMedium',
          color: '#D1DCDD',
          align: 'right',
          rich: {
            percent: {
              fontSize: 12,
              fontFamily: 'DemoLight',
            },
          }
        },
        itemStyle: {
          shadowColor: 'rgba(0,33,59,0.6)',
          shadowBlur: 8,
        },
        select: {
          itemStyle: {
            borderWidth: 0,
            color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#D1DCDD' }, { offset: 1, color: '#799A9D' }])
          },
          label: {
            show: true,
            opacity: 1,
          }
        },
        selectedMode: true,
        data: []
      },
      {
        id: 'current',
        name: 'current',
        type: 'bar',
        label: {
          position: 'top',
          fontSize: 16,
          fontFamily: 'DemoMedium',
          color: '#00BDDE',
          align: 'center',
          formatter: function (params: any): string {
            let label = truncateDecimal(params.value);
            label += params.data["percent"] === 0 || params.data["percent"] ? `\n{percent|${params.data["percent"]}%}` : '';
            return label;
          },
          rich: {
            percent: {
              fontSize: 12,
              fontFamily: 'DemoLight',
            },
          }
        },
        itemStyle: {
          shadowColor: 'rgba(0,33,59,0.6)',
          shadowBlur: 8,
        },
        selectedMode: true,
        select: {
          itemStyle: {
            borderWidth: 0,
            color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#00DEEF' }, { offset: 1, color: '#1A4F69' }])
          },
          label: {
            show: true,

          }
        },
        data: []
      },
      {
        id: 'partial',
        type: 'bar',
        boundaryGap: true,
        stack: 'x',
        label: {
          position: 'top',
          fontSize: 16,
          fontFamily: 'DemoMedium',
          color: '#768B95',
          align: 'right',
          rich: {
            percent: {
              fontSize: 12,
              fontFamily: 'DemoLight',
            },
          }
        },
        itemStyle: {
          shadowColor: 'rgba(0,33,59,0.6)',
          shadowBlur: 8,
          opacity: 0.18
        },
        select: {
          itemStyle: {
            opacity: 0.4,
            borderWidth: 0,
            color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#D1DCDD' }, { offset: 1, color: '#799A9D' }])
          },
          label: {
            show: true,
            opacity: 1,
          }
        },
        selectedMode: true,
        data: []
      },
      {
        type: 'bar',
        xAxisIndex: 2,
        dimensions: ["colGroupName"]
      },
    ]
  };

  constructor() { }

  ngOnInit(): void {
    this.checkGraphType();
    this.initGraphOption();
  }

  ngOnChanges(changes: SimpleChanges): void {

    if(changes['userData'] && this.userData) {
      const dataIndex = this.graph.data.findIndex(item => item.colName === this.userData.colName);
      if(dataIndex > -1) {
        this.selectedColumnIndex = dataIndex
        this.selectBar(dataIndex, false);
      }
    }

    if (changes['selectedColumnIndex'] && this.selectedColumnIndex !== undefined && this.selectedColumnIndex !== null && this.selectedColumnIndex > -1) {
      this.selectBar(this.selectedColumnIndex);
    }

    if (changes['isSecondary']) {
      this.mergeOptions = {
        series: [
          {
            name: 'current',
            select: {
              itemStyle: {
                color: this.isSecondary ?
                  new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#0093EF' }, { offset: 1, color: '#1E478D' },])
                  :
                  new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#00DEEF' }, { offset: 1, color: '#1A4F69' }])
              },
              label: {
                color: this.isSecondary ? '#038BE5' : '#00BDDE'
              }
            }
          }
        ]
      };
    }
  }

  private checkGraphType(): void {
    if (this.graph.data[0].compare) {
      this.type = VerticalGraphType.COMPARE_BAR;
    }
  }

  private formatYAxisLabel(value: number, index: number): string {
    return value !== 0 && index !== 0 ? `${value}${this.graph.columnType ? this.graph.columnType : ''}` : "";
  }

  private mapXAxisData(graphData: VerticalGraphItem[]): String[] {
    return graphData.map(item => item.colName);
  }

  private mapCurrentData(graphData: VerticalGraphItem[]): any[] {
    return graphData.map((item) => ({
      value: item.current.value,
      percent: item.current.percent,
      isPreliminary: item.isPreliminary,
      label: {
        align: this.type === VerticalGraphType.SIMPLE_BAR ? 'center' : (item.compare && item.current.value < item.compare.value) || (item.partial && item.current.value < item.partial.value) ? 'left' : 'left'
      },
      select: {
        itemStyle: {
          color: item.current.value < 0 ? new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#DA4A4F' }, { offset: 1, color: '#FF6F74' }]) : undefined
        },
        label: {
          color: item.current.value < 0 ? '#FF6F74' : undefined,
          rich: {
            percent: {
              color: item.current.value < 0 ? '#FF6F74' : undefined,
            }
          }
        }
      }
    }))
  }

  private mapComapreData(graphData: VerticalGraphItem[]): any[] {
    return graphData.map((item) => {
      if (item.partial && item.compare) {
        if ((item.compare.value > 0 && item.partial.value > 0 && item.compare.value > item.partial.value) ||
          (item.compare.value < 0 && item.partial.value < 0 && item.compare.value < item.partial.value) ||
          (item.compare.value === item.partial.value)) {
          return {
            value: item.partial.value,
            select: {
              label: {
                offset: this.getOffset(item.partial.value, item.compare.value),
                // color: item.partial.value < 0 ? '#FF6F74' : undefined
              }
            }
          };
        }

        if ((item.compare.value > 0 && item.partial.value > 0 && item.compare.value < item.partial.value) ||
          (item.compare.value < 0 && item.partial.value < 0 && item.compare.value > item.partial.value)) {
          return {
            value: item.compare.value,
            itemStyle: {
              color: '#024266'
            },
            select: {
              itemStyle: {
                color: '#436378'
              },
              label: {
                color: '#768B95',
                offset: this.getOffset(item.compare.value, item.partial.value),
              }
            }
          }
        }
      }

      return {
        value: item.compare?.value,
        label: {
          align: item.isFuture && (item.current.value === 0) ? 'center' : undefined
        },
        itemStyle: {
          opacity: item.isFuture ? 0.18 : undefined,
          color: item.isFuture ? new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: 'rgba(209,220,221,0.18)' }, { offset: 1, color: '#799A9D' }]) : undefined
        },
        select: {
          itemStyle: {
            opacity: item.isFuture ? 0.4 : 1,
            color: item.isFuture ?
              new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: 'rgba(209,220,221,0.18)' }, { offset: 1, color: '#799A9D' }])
              : undefined
            // color: item.isFuture ?
            //   new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: 'rgba(209,220,221,0.18)' }, { offset: 1, color: '#799A9D' }])
            //   : item.compare && item.compare?.value < 0 ?
            //     new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#DA4A4F' }, { offset: 1, color: '#FF6F74' }])
            //     : undefined
          },
          label: {
            color: item.isFuture ? '#768B95' : undefined
            // color: item.isFuture ? '#768B95' : item.compare && item.compare?.value < 0 ? '#FF6F74' : undefined
          }
        }
      };
    });
  }

  private mapPartialData(graphData: VerticalGraphItem[]): any[] {
    return graphData.map((item) => {
      if (item.partial && item.compare) {
        if ((item.compare.value > 0 && item.partial.value > 0 && item.compare.value > item.partial.value) ||
          (item.compare.value < 0 && item.partial.value < 0 && item.compare.value < item.partial.value) ||
          (item.compare.value === item.partial.value)) {
          return {
            value: item.compare.value - item.partial.value,
            label: {
              formatter: [truncateDecimal(item.compare.value)].join('\n'),
              offset: this.getOffset(item.compare.value, item.partial.value),
              // lineHeight: 32,
            }
          };
        }

        if ((item.compare.value > 0 && item.partial.value > 0 && item.compare.value < item.partial.value) ||
          (item.compare.value < 0 && item.partial.value < 0 && item.compare.value > item.partial.value)) {
          return {
            value: item.partial.value - item.compare.value,
            label: {
              formatter: [truncateDecimal(item.partial.value)].join('\n'),
              lineHeight: 0,
              offset: this.getOffset(item.partial.value, item.compare.value),
            },
            itemStyle: {
              opacity: 1,
              color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#006F9F' }, { offset: 1, color: '#1A4F69' },])
            },
            select: {
              itemStyle: {
                opacity: 1,
                color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#D1DCDD' }, { offset: 1, color: '#799A9D' }])
                /*
                color: item.partial.value > 0 ? new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#D1DCDD' }, { offset: 1, color: '#799A9D' }]) :
                  new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#DA4A4F' }, { offset: 1, color: '#FF6F74' }])
                */
              },
              label: {
                color: '#D1DCDD',
                // color: item.partial.value > 0 ? '#D1DCDD' : '#FF6F74'
              }
            }
          }
        }
      }

      return {
        value: item.partial?.value,
        itemStyle: {
          opacity: 1,
          color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#006F9F' }, { offset: 1, color: '#1A4F69' },])
        },
        select: {
          itemStyle: {
            opacity: 1,
            color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#D1DCDD' }, { offset: 1, color: '#799A9D' }])
            // color: item.partial && item.partial?.value < 0 ?
            //   new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#DA4A4F' }, { offset: 1, color: '#FF6F74' }]) :
            //   new echarts.graphic.LinearGradient(0, 0, 1, 1, [{ offset: 0, color: '#D1DCDD' }, { offset: 1, color: '#799A9D' }])
          },
          label: {
            color: '#D1DCDD'
            // color: item.partial && item.partial?.value < 0 ? '#FF6F74' : '#D1DCDD'
          }
        }
      }
    });
  }

  private getOffset(current: number, second: number,): [number, number | string] {
    const offset = 16;
    const minChange = 8;
    const change = current - second;

    if (current < 0 && second < 0) {
      if (Math.abs(change) > minChange || current > second) {
        return [0, 0];
      } else {
        return [0, (Math.abs(current + second) + offset ) * -1 ];
      }
    }

    if (Math.abs(change) > minChange) {
      return [0, 0];
    }

    if (current > second) {
      return [0, -offset];
    } else {
      return [0,0];
    }
  }

  private initGraphOption(): void {
    let isPreliminary = false;

    this.baseOptions.dataset = {
      source: [...this.graph.data]
    };
    this.baseOptions.xAxis[0].data = this.mapXAxisData(this.graph.data);
    this.baseOptions.xAxis[1].data = this.graph.data.map(item => {
      if (item.isPreliminary) {
        isPreliminary = true;
        return { value: 'P' };
      } else {
        return { value: '-', textStyle: { opacity: 0 } };
      }
    });
    this.baseOptions.xAxis[2].axisTick.show = !this.graph.data.every(item => item.colGroupName === this.graph.data[0].colGroupName);

    if (this.graph.data[0].colGroupName) {
      if (isPreliminary) {
        this.baseOptions.xAxis[2].axisLabel.margin = 10;
        this.baseOptions.xAxis[2].axisTick.length = 25;
        this.baseOptions.grid.bottom = 3;
      }
      else {
        this.baseOptions.grid.bottom = 13;
      }
    }

    switch (this.type) {
      case VerticalGraphType.SIMPLE_BAR: {
        this.optionSimpleBar.series[0].data = this.mapCurrentData(this.graph.data);
        this.optionSimpleBar.series[0].barMaxWidth = this.graph.data.length < 12 ? 18 : 9;
        this.options = { ...this.baseOptions, ...this.optionSimpleBar };
        break;
      }
      case VerticalGraphType.COMPARE_BAR: {
        this.optionCompareBar.series[0].data = this.mapComapreData(this.graph.data);
        this.optionCompareBar.series[1].data = this.mapCurrentData(this.graph.data);
        this.optionCompareBar.series[2].data = this.mapPartialData(this.graph.data);
        this.optionCompareBar.series[0].barMaxWidth = this.graph.data.length < 12 ? 18 : 7;
        this.optionCompareBar.series[1].barMaxWidth = this.graph.data.length < 12 ? 18 : 9;
        this.optionCompareBar.series[2].barMaxWidth = this.graph.data.length < 12 ? 18 : 7;
        this.options = { ...this.baseOptions, ...this.optionCompareBar };
        break;
      }
    }
  }

  public onChartInit(ec: any): void {
    this.myChart = ec;
    this.initChartEvents(ec);
  }

  private initChartEvents(chartInstance: any): void {
    if (this.selectedColumnIndex !== undefined && this.selectedColumnIndex !== -1) {
      this.selectBar(this.selectedColumnIndex);
    }
    else {
      this.selectDefaultBar();
    }

    chartInstance.getZr().on('click', (params: any) => {
      const pointInPixel = [params.offsetX, params.offsetY];
      const pointInGrid = chartInstance.convertFromPixel('grid', pointInPixel);
      if(pointInGrid[0] > -1 && pointInGrid[0] < this.graph.data.length) {
          chartInstance.dispatchAction({ type: 'select', dataIndex: pointInGrid[0] });
          this.onBarSelect.emit(pointInGrid[0]);
          this.onUserBarSelect.emit(this.graph.data[pointInGrid[0]]);
        }
    });
  }

  private selectBar(index: number, emitresult = true) {
    //TODO: test cdr instead of timeout option
    setTimeout(() => {
      this.myChart?.dispatchAction({ type: 'select', dataIndex: index });
      // emitresult && this.onBarSelect.emit(index);
    })
  }

  private selectDefaultBar(): void {
    if (this.myChart) {
      let defaultIndex = this.graph.data.findIndex(item => item.isDefault);
      this.selectBar(defaultIndex);
    }
  }
}
